import classNames from 'classnames';
import React, { useEffect, useRef } from 'react';

import Icon, { ICON_TYPES } from '@commons/Icon';

import { CookieBannerVersion, isV1 } from '@components/CookieBanner/shared/utils';

import stripHtml from '@utils/stripHtml';

import styles from './cookieBannerHeader.scss';

//----------------------------------------------------------------------
// TYPES
//----------------------------------------------------------------------

interface CookieBannerHeaderProps {
  onBack: () => void;
  onClose: () => void;
  showBackButton: boolean;
  backButtonText: string;
  closeButtonText: string;
  version: CookieBannerVersion;
  isRTL: boolean;
  // TODO: properly type src/components/StackNavigator/StackNavigatorProvider.jsx.
  navigation: any;
  pathname: string;
}

//----------------------------------------------------------------------
// UI
//----------------------------------------------------------------------

function CookieBannerHeader({
  onBack,
  onClose,
  showBackButton,
  backButtonText,
  closeButtonText,
  version,
  isRTL = false,
  pathname,
  navigation
}: CookieBannerHeaderProps) {
  const backButtonRef = useRef<HTMLButtonElement>(null);

  const wrapperClassName = classNames(styles.wrapper, {
    [styles.showBackButton]: showBackButton
  });

  const classes = classNames(styles.header, {
    [styles.showBackButton]: showBackButton
  });

  useEffect(() => {
    if (pathname && showBackButton && backButtonRef.current) {
      backButtonRef.current.focus();
    }
  }, [pathname]);

  const handleOnBack = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    navigation.pop();
  };

  const renderBackButton = () => {
    if (!showBackButton) {
      return null;
    }

    const iconType = isRTL ? ICON_TYPES.ARROW_RIGHT : ICON_TYPES.ARROW_LEFT;

    if (isV1(version)) {
      return (
        <button
          className={styles.backButton}
          onClick={handleOnBack}
          aria-label={stripHtml(backButtonText)}
          ref={backButtonRef}>
          <Icon type={iconType} />
        </button>
      );
    }

    return (
      <button className={styles.backButton} onClick={handleOnBack} ref={backButtonRef}>
        {!isRTL && <Icon type={iconType} />}
        <span dangerouslySetInnerHTML={{ __html: backButtonText }} />
        {isRTL && <Icon type={iconType} />}
      </button>
    );
  };

  return (
    <div className={wrapperClassName}>
      <div className={classes}>
        {renderBackButton()}
        {isV1(version) && (
          <button aria-label={stripHtml(closeButtonText)} type="button" onClick={onClose}>
            <Icon type={ICON_TYPES.CLOSE} />
          </button>
        )}
      </div>
    </div>
  );
}

export default CookieBannerHeader;
